import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  EMPTY_ENTRY,
  subscribeToCylindoStore,
  getCylindoEntry,
  requestCylindoFetch,
} from './utils';

// deferFetch: start Cylindo config request after window load + idle (not in render).
const useCylindoCache = (productId, isCylindoProduct, deferFetch = false) => {
  useEffect(() => {
    if (!deferFetch || !productId || !isCylindoProduct) {
      return undefined;
    }

    let cancelled = false;
    let cancelScheduled = () => {};

    const runFetch = () => {
      if (!cancelled) {
        requestCylindoFetch(productId);
      }
    };

    const afterLoad = () => {
      if (cancelled) {
        return;
      }
      if (typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(runFetch, { timeout: 5000 });
        cancelScheduled = () => {
          if (typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(id);
          }
        };
      } else {
        const id = window.setTimeout(runFetch, 2000);
        cancelScheduled = () => clearTimeout(id);
      }
    };

    if (typeof document !== 'undefined' && document.readyState === 'complete') {
      afterLoad();
    } else if (typeof window !== 'undefined') {
      window.addEventListener('load', afterLoad);
    }

    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('load', afterLoad);
      }
      cancelScheduled();
    };
  }, [deferFetch, productId, isCylindoProduct]);

  useEffect(() => {
    if (!deferFetch && productId && isCylindoProduct) {
      requestCylindoFetch(productId);
    }
  }, [deferFetch, productId, isCylindoProduct]);

  const getSnapshot = useCallback(() => getCylindoEntry(productId), [
    productId,
  ]);

  const getServerSnapshot = useCallback(() => EMPTY_ENTRY, []);

  const { config, version, loading } = useSyncExternalStore(
    subscribeToCylindoStore,
    getSnapshot,
    getServerSnapshot
  );

  return { config, version, loading };
};

export default useCylindoCache;
